import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api, inr } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1756483488645-5973a1a92e33?w=1400";
const LEHENGA = "https://images.unsplash.com/photo-1716504628204-47f2df8d2634?w=800";
const KIDS_G = "https://images.unsplash.com/photo-1597294151491-1d22b38698d6?w=800";
const KIDS_B = "https://images.unsplash.com/photo-1561987619-1c9a98f83752?w=800";
const TEXTURE = "https://images.unsplash.com/photo-1652722464455-ec026ef74703?w=1400";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data.items)); }, []);

  return (
    <div className="bg-[#FCE4EC]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-16 grid md:grid-cols-12 gap-5 md:gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="eyebrow mb-3 md:mb-4 flex items-center gap-2"><Sparkles size={14} /> Festive '26 Edit</div>
            <h1 className="font-serif text-[2rem] sm:text-5xl lg:text-6xl leading-[1.05] text-[#0B1F3A] tracking-tight">
              Heritage <span className="italic text-[#D4AF37]">reimagined</span> for the modern muse.
            </h1>
            <p className="mt-4 md:mt-6 text-sm md:text-base text-[#1E293B]/80 max-w-md leading-relaxed">
              Handloom-woven sarees, editorial lehengas, and heirloom-inspired pieces for women & kids — delivered across India.
            </p>
            <div className="mt-6 md:mt-8 grid grid-cols-2 sm:flex sm:flex-row gap-3">
              <Link to="/shop/women" className="bg-[#0B1F3A] text-[#FFF9E6] px-4 sm:px-7 py-3.5 sm:py-4 text-xs sm:text-sm tracking-widest uppercase hover:bg-[#163056] transition flex items-center justify-center gap-2" data-testid="hero-shop-women">
                Shop Women <ArrowRight size={14} />
              </Link>
              <Link to="/shop/kids" className="border border-[#0B1F3A] text-[#0B1F3A] px-4 sm:px-7 py-3.5 sm:py-4 text-xs sm:text-sm tracking-widest uppercase hover:bg-[#0B1F3A] hover:text-[#FFF9E6] transition text-center" data-testid="hero-shop-kids">
                Explore Kids
              </Link>
            </div>
            <div className="mt-8 md:mt-10 grid grid-cols-3 gap-3 md:gap-8 text-[10px] md:text-xs tracking-widest uppercase text-[#1E293B]/60">
              <div><div className="font-serif text-xl md:text-2xl text-[#D4AF37] normal-case leading-none mb-1">1000+</div>Curated pieces</div>
              <div><div className="font-serif text-xl md:text-2xl text-[#D4AF37] normal-case leading-none mb-1">₹0</div>Free shipping</div>
              <div><div className="font-serif text-xl md:text-2xl text-[#D4AF37] normal-case leading-none mb-1">7-day</div>Free returns</div>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 relative">
            <div className="aspect-[4/5] md:aspect-[4/5] relative overflow-hidden">
              <img src={HERO} className="w-full h-full object-cover" alt="Festive edit" loading="eager" />
              <div className="absolute inset-0 border-4 md:border-[6px]" style={{ borderColor: "rgba(212,175,55,0.3)" }} />
            </div>
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-[#FCE4EC] p-4 border" style={{ borderColor: "rgba(212,175,55,0.4)" }}>
              <div className="eyebrow mb-1">New Drop</div>
              <div className="font-serif text-lg text-[#0B1F3A]">Banarasi Handloom Series</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category tiles */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-16">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">Curated Collections</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#0B1F3A]">Shop by universe</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {[
            { label: "Women", tag: "Sarees, Lehengas", img: LEHENGA, link: "/shop/women", key: "women", span: "col-span-2 md:col-span-1" },
            { label: "Kids · Girls", tag: "Festive lehengas", img: KIDS_G, link: "/shop/kids?subcategory=Girls Lehenga", key: "girls", span: "" },
            { label: "Kids · Boys", tag: "Kurtas & sherwanis", img: KIDS_B, link: "/shop/kids?subcategory=Boys Kurta", key: "boys", span: "" },
          ].map((c) => (
            <Link key={c.key} to={c.link} className={`group relative aspect-[4/5] overflow-hidden block ${c.span}`} data-testid={`cat-tile-${c.key}`}>
              <img src={c.img} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/80 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 text-[#FFF9E6]">
                <div className="eyebrow text-[9px] md:text-xs" style={{ color: "#D4AF37" }}>{c.tag}</div>
                <div className="font-serif text-xl md:text-3xl mt-1">{c.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="eyebrow mb-2">Editor's Picks</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0B1F3A]">Featured this season</h2>
          </div>
          <Link to="/shop" className="text-sm tracking-widest uppercase text-[#0B1F3A] hover:text-[#D4AF37] hidden md:flex items-center gap-2" data-testid="view-all">View all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {featured.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      {/* Story Behind the Stitch */}
      <section className="relative my-12 md:my-24">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${TEXTURE})`, backgroundSize: "cover" }} />
        <div className="relative bg-[#0B1F3A]/95 py-12 md:py-24">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-[#FFF9E6]">
            <div className="eyebrow mb-3 md:mb-4">The Nisa Story</div>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl leading-tight mb-4 md:mb-6">Story <span className="italic text-[#D4AF37]">behind</span> the stitch</h2>
            <div className="gold-line max-w-xs mx-auto mb-4 md:mb-6" />
            <p className="text-sm md:text-base text-[#FFF9E6]/80 leading-relaxed max-w-2xl mx-auto">
              Every piece begins in a weaver's home in Varanasi, Chanderi, or the villages of Bengal. We work directly with master artisans — no middlemen — so heritage stays alive and craftsmanship stays fair.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
